/**
 * Factura de compra / conduce de Cuentas x Pagar en hoja carta (8.5x11).
 *
 * La imprimen el registro de la factura general (ares8510), la compra de
 * inventario bulk (con motores) y la reimpresión desde el mantenimiento de
 * facturas — todas con los datos de /api/cxp_factura_detalle o equivalentes:
 * `{ factura, lineas, compania }`.
 */

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Logo del membrete: solo data URLs de imagen (lo que guarda configuración);
// cualquier otra cosa se ignora para no inyectar HTML en la impresión.
const logoImg = (comp) =>
  comp && typeof comp.logo === "string" && comp.logo.startsWith("data:image/")
    ? `<img class="logo" src="${comp.logo}" alt="" />`
    : "";

const CSS_LOGO = `
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 60px; max-width: 170px; object-fit: contain; }`;

function imprimirHtml(html) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export function imprimirFacturaCxp(det) {
  const f = det.factura || {};
  const compania = det.compania || {};
  const lineas = det.lineas || [];
  const NAVY = "#18586b";
  const filasLineas = lineas
    .map(
      (l, i) => `<tr>
          <td>${i + 1}</td>
          <td>${l.chasis || ""}</td>
          <td>${l.marca || ""}</td>
          <td>${l.modelo || ""}</td>
          <td>${l.colore || ""}</td>
          <td class="num">${fmtMoney(l.valor)}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Factura de Compra CXP</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      ${CSS_LOGO}
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 200px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .anulada { margin-top: 6px; display: inline-block; border: 2px solid #C62828; color: #C62828;
                 font-weight: bold; padding: 2px 10px; border-radius: 4px; letter-spacing: 2px; }
      .suplidor { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                  display: flex; gap: 30px; flex-wrap: wrap; }
      .suplidor span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .suplidor b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      th.num, td.num, table.lineas th.num { text-align: right; }
      .totales { margin: 12px 0; display: flex; justify-content: flex-end; gap: 10px; }
      .totales .monto { background: #f0f5f8; border: 1px solid #d7dee5; border-radius: 6px;
                        padding: 8px 16px; font-size: 12px; color: #4a5866; }
      .totales .monto b { display: block; font-size: 14px; color: ${NAVY}; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(compania)}
          <div>
            <h1>${compania.nombre || ""}</h1>
            ${compania.direccion ? `<p>${compania.direccion}</p>` : ""}
            <p>${[compania.rnc ? `RNC: ${compania.rnc}` : "", compania.telefono ? `Tel: ${compania.telefono}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Factura de Compra · Conduce</div>
          <p>Cuentas por Pagar${f.tipofactura ? ` — ${f.tipofactura}` : ""}</p>
          <p># Factura: <b>${f.nfactura || ""}</b></p>
          <p>Fecha: <b>${f.fecha || ""}</b></p>
          ${f.tipocomprobante ? `<p>Comprobante: <b>${f.tipocomprobante}</b></p>` : ""}
          ${f.anulada ? '<div class="anulada">ANULADA</div>' : ""}
        </div>
      </div>
      <div class="suplidor">
        <div><span>Suplidor</span><b>${f.suplidornombre || ""}</b></div>
        ${f.suplidorrnc ? `<div><span>RNC Suplidor</span><b>${f.suplidorrnc}</b></div>` : ""}
        ${f.concepto ? `<div><span>Concepto</span><b>${f.concepto}</b></div>` : ""}
      </div>
      ${
        lineas.length
          ? `<table class="lineas">
        <tr>
          <th style="width:28px">#</th>
          <th>Chasis</th>
          <th>Marca</th>
          <th>Modelo</th>
          <th>Color</th>
          <th class="num">Valor</th>
        </tr>
        ${filasLineas}
      </table>`
          : ""
      }
      <div class="totales">
        ${
          f.vplicado && parseFloat(String(f.vplicado).replace(/,/g, "")) > 0
            ? `<div class="monto">Pagado<b>RD$ ${fmtMoney(f.vplicado)}</b></div>
        <div class="monto">Balance<b>RD$ ${fmtMoney(f.balance)}</b></div>`
            : ""
        }
        <div class="monto">Total factura<b>RD$ ${fmtMoney(f.valor)}</b></div>
      </div>
      <div class="firmas">
        <div>Preparado por</div>
        <div>Recibido conforme</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

/**
 * Documento de Devolución a Suplidor (QA): comprobante impreso al devolver
 * un chasis de una factura CxP. `dev` es el objeto `devolucion` que devuelve
 * /api/cxp_chasis_eliminar; `compania` el membrete {nombre, direccion, rnc,
 * telefono} (acepta string con solo el nombre).
 */
export function imprimirDevolucionCxp(dev, compania) {
  const NAVY = "#18586b";
  const comp = typeof compania === "string" ? { nombre: compania } : compania || {};
  if (!comp.nombre) comp.nombre = localStorage.getItem("nombrenegocio") || "";
  const esc = (s) =>
    String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Devolución a Suplidor</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      ${CSS_LOGO}
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .bloque { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      th.num, td.num { text-align: right; }
      .motivo { margin: 12px 0; font-size: 11px; color: #4a5866; }
      .firmas { display: flex; justify-content: space-around; margin-top: 80px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(comp)}
          <div>
            <h1>${esc(comp.nombre)}</h1>
            ${comp.direccion ? `<p>${esc(comp.direccion)}</p>` : ""}
            <p>${[comp.rnc ? `RNC: ${esc(comp.rnc)}` : "", comp.telefono ? `Tel: ${esc(comp.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Devolución a Suplidor</div>
          <p># Devolución: <b>${esc(dev.id)}</b></p>
          <p>Fecha: <b>${esc(dev.fecha)}</b></p>
          <p>Factura: <b>${esc(dev.nfactura)}</b></p>
        </div>
      </div>
      <div class="bloque">
        <div><span>Suplidor</span><b>${esc(dev.suplidornombre)}</b></div>
        <div><span>Registrada por</span><b>${esc(dev.usuario)}</b></div>
      </div>
      <table class="lineas">
        <tr><th>Chasis</th><th>Marca</th><th>Modelo</th><th>Color</th><th class="num">Valor</th></tr>
        <tr>
          <td>${esc(dev.chasis)}</td>
          <td>${esc(dev.marca)}</td>
          <td>${esc(dev.modelo)}</td>
          <td>${esc(dev.colore)}</td>
          <td class="num">${fmtMoney(dev.valor)}</td>
        </tr>
      </table>
      ${dev.motivo ? `<p class="motivo"><b>Motivo:</b> ${esc(dev.motivo)}</p>` : ""}
      <div class="firmas">
        <div>Entregado por${dev.usuario ? `<br/><b>${esc(dev.usuario)}</b>` : ""}</div>
        <div>Recibido por (suplidor)</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

/**
 * Conduce de Devolución: documento agrupado que ampara la salida de VARIOS
 * motores consignados devueltos en una misma operación. `devoluciones` es la
 * lista que devuelve /api/consignaciones_devolverlote (todas comparten lote,
 * factura y suplidor); el folio del documento es el lote.
 */
export function imprimirConduceDevolucion(devoluciones, compania) {
  if (!devoluciones || !devoluciones.length) return;
  const NAVY = "#18586b";
  const comp = typeof compania === "string" ? { nombre: compania } : compania || {};
  if (!comp.nombre) comp.nombre = localStorage.getItem("nombrenegocio") || "";
  const esc = (s) =>
    String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const d0 = devoluciones[0];
  const total = devoluciones.reduce((s, d) => s + (parseFloat(d.valor) || 0), 0);
  const filas = devoluciones
    .map(
      (d, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(d.chasis)}</td>
        <td>${esc(d.marca)}</td>
        <td>${esc(d.modelo)}</td>
        <td>${esc(d.colore)}</td>
        <td class="num">${fmtMoney(d.valor)}</td>
      </tr>`
    )
    .join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Conduce de Devolución</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      ${CSS_LOGO}
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .bloque { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      th.num, td.num { text-align: right; }
      .total { text-align: right; margin-top: 8px; font-size: 12px; }
      .motivo { margin: 12px 0; font-size: 11px; color: #4a5866; }
      .firmas { display: flex; justify-content: space-around; margin-top: 90px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(comp)}
          <div>
            <h1>${esc(comp.nombre)}</h1>
            ${comp.direccion ? `<p>${esc(comp.direccion)}</p>` : ""}
            <p>${[
              comp.rnc ? `RNC: ${esc(comp.rnc)}` : "",
              comp.telefono ? `Tel: ${esc(comp.telefono)}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Conduce de Devolución</div>
          <p># Conduce: <b>${esc(d0.lote ?? d0.id)}</b></p>
          <p>Fecha: <b>${esc(d0.fecha)}</b></p>
          <p>Factura: <b>${esc(d0.nfactura)}</b></p>
        </div>
      </div>
      <div class="bloque">
        <div><span>Consignante</span><b>${esc(d0.suplidornombre)}</b></div>
        <div><span>Motores devueltos</span><b>${devoluciones.length}</b></div>
        <div><span>Registrado por</span><b>${esc(d0.usuario)}</b></div>
      </div>
      <table class="lineas">
        <tr><th style="width:28px">#</th><th>Chasis</th><th>Marca</th><th>Modelo</th><th>Color</th><th class="num">Valor</th></tr>
        ${filas}
      </table>
      <div class="total">Total devuelto: <b>RD$ ${fmtMoney(total)}</b></div>
      ${d0.motivo ? `<p class="motivo"><b>Motivo:</b> ${esc(d0.motivo)}</p>` : ""}
      <div class="firmas">
        <div>Entregado por${d0.usuario ? `<br/><b>${esc(d0.usuario)}</b>` : ""}</div>
        <div>Recibido por${
          d0.recibidopor ? `<br/><b>${esc(d0.recibidopor)}</b>` : " (consignante)"
        }</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

export default imprimirFacturaCxp;

/**
 * Recibo de compra informal (inventario bulk, condición "Compra informal"):
 * la compra sin factura del vendedor. Lo firma el vendedor al recibir el
 * pago, así que lleva su documento, los chasis, el monto y una declaración
 * de propiedad. `det` es /api/cxp_factura_detalle; `vendedor` y `pago` los
 * datos del formulario (documento, nombre, teléfono, dirección; forma de
 * pago, banco y referencia).
 */
export function imprimirCompraInformal(det, vendedor, pago) {
  const f = det.factura || {};
  const compania = det.compania || {};
  const lineas = det.lineas || [];
  const v = vendedor || {};
  const pg = pago || {};
  const NAVY = "#18586b";
  const esc = (s) =>
    String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const filas = lineas
    .map(
      (l, i) => `<tr>
          <td>${i + 1}</td>
          <td>${esc(l.chasis)}</td>
          <td>${esc(l.marca)}</td>
          <td>${esc(l.modelo)}</td>
          <td>${esc(l.colore)}</td>
          <td class="num">${fmtMoney(l.valor)}</td>
        </tr>`
    )
    .join("");
  const formaPago = [pg.tipodepago, pg.banco, pg.referencia ? `Ref. ${pg.referencia}` : ""]
    .filter(Boolean)
    .join(" · ");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Recibo de Compra</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      ${CSS_LOGO}
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 200px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .bloque { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      th.num, td.num, table.lineas th.num { text-align: right; }
      .totales { margin: 12px 0; display: flex; justify-content: flex-end; }
      .totales .monto { background: #f0f5f8; border: 1px solid #d7dee5; border-radius: 6px;
                        padding: 8px 16px; font-size: 12px; color: #4a5866; }
      .totales .monto b { display: block; font-size: 14px; color: ${NAVY}; }
      .declara { margin-top: 14px; font-size: 11.5px; line-height: 1.55; text-align: justify; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 230px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(compania)}
          <div>
            <h1>${esc(compania.nombre)}</h1>
            ${compania.direccion ? `<p>${esc(compania.direccion)}</p>` : ""}
            <p>${[compania.rnc ? `RNC: ${esc(compania.rnc)}` : "", compania.telefono ? `Tel: ${esc(compania.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Recibo de Compra</div>
          <p>Compra informal (sin factura)</p>
          <p># Compra: <b>${esc(f.nfactura)}</b></p>
          <p>Fecha: <b>${esc(f.fecha)}</b></p>
        </div>
      </div>
      <div class="bloque">
        <div><span>Vendedor</span><b>${esc(v.nombre || f.suplidornombre)}</b></div>
        <div><span>Documento</span><b>${esc(v.documento || f.suplidorrnc)}</b></div>
        ${v.telefono ? `<div><span>Teléfono</span><b>${esc(v.telefono)}</b></div>` : ""}
        ${v.direccion ? `<div><span>Dirección</span><b>${esc(v.direccion)}</b></div>` : ""}
        ${formaPago ? `<div><span>Forma de pago</span><b>${esc(formaPago)}</b></div>` : ""}
      </div>
      <table class="lineas">
        <tr>
          <th style="width:28px">#</th>
          <th>Chasis</th>
          <th>Marca</th>
          <th>Modelo</th>
          <th>Color</th>
          <th class="num">Valor</th>
        </tr>
        ${filas}
      </table>
      <div class="totales">
        <div class="monto">Total pagado<b>RD$ ${fmtMoney(f.valor)}</b></div>
      </div>
      <div class="declara">
        Yo, <b>${esc(v.nombre || f.suplidornombre)}</b>, portador(a) del documento
        <b>${esc(v.documento || f.suplidorrnc)}</b>, declaro ser el legítimo propietario
        ${lineas.length === 1 ? "del vehículo descrito" : "de los vehículos descritos"} en este recibo,
        que lo${lineas.length === 1 ? "" : "s"} vendo libre de gravámenes, deudas y reclamaciones de terceros,
        y que recibo conforme la suma de <b>RD$ ${fmtMoney(f.valor)}</b> como pago total de esta venta.
      </div>
      <div class="firmas">
        <div>Firma del vendedor</div>
        <div>Recibido por ${esc(compania.nombre)}</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}
